/**
 * Prescriptive easement law by state.
 *
 * General educational reference only. Prescription periods, elements, and
 * exceptions change, and several states set the period by case law rather than
 * by statute. Every entry links to the free, official state code or legislative
 * site so the current text can be read directly. No licensed or paid legal data
 * provider is used.
 */

export type StateEasementLaw = {
  /** USPS two-letter code, used as the URL value. */
  stateCode: string;
  stateName: string;
  /** Years of open, notorious, continuous, adverse use generally required. */
  years: number;
  /** Shown when the state's adverse possession clock differs from prescription. */
  adversePossessionYears?: number;
  /** Statute or leading authority commonly cited for the period. */
  citation: string;
  /** Free official state code or legislature site. */
  officialUrl: string;
  /** Plain-English notes on state-specific quirks. */
  notes: string[];
};

const PUBLIC_LAND_NOTE =
  "Land owned by the state or a municipality generally cannot be taken by prescription.";

export const PRESCRIPTIVE_EASEMENT_LAW: StateEasementLaw[] = [
  {
    stateCode: "AL",
    stateName: "Alabama",
    years: 20,
    adversePossessionYears: 10,
    citation: "Ala. Code § 6-5-200; common law prescription",
    officialUrl: "https://alison.legislature.state.al.us/code-of-alabama",
    notes: [
      "Twenty years of adverse use is the general prescriptive period.",
      "Ten years applies to adverse possession with color of title and payment of taxes.",
      PUBLIC_LAND_NOTE,
    ],
  },
  {
    stateCode: "AK",
    stateName: "Alaska",
    years: 10,
    citation: "Alaska Stat. § 09.45.052",
    officialUrl: "https://www.akleg.gov/basis/statutes.asp",
    notes: [
      "Ten years of uninterrupted adverse use; a seven-year path exists with recorded color of title.",
      "Claims against land under the Alaska Native Claims Settlement Act face additional limits.",
    ],
  },
  {
    stateCode: "AZ",
    stateName: "Arizona",
    years: 10,
    citation: "Ariz. Rev. Stat. § 12-526",
    officialUrl: "https://www.azleg.gov/arstitle/",
    notes: [
      "Ten years of open and adverse use, with shorter periods available under other statutes with color of title and tax payment.",
      "Use that is merely permissive never ripens, no matter how long it continues.",
    ],
  },
  {
    stateCode: "AR",
    stateName: "Arkansas",
    years: 7,
    citation: "Gazaway v. Pugh, 69 Ark. App. 297, 12 S.W.3d 662 (2000) (case law; no statute sets the period)",
    officialUrl: "https://www.arkleg.state.ar.us/ArkansasLaw",
    notes: [
      "Arkansas courts apply a seven-year period that runs from when the owner knew, or the circumstances show the owner should have known, that the use was adverse.",
      "Use that begins with the owner's permission does not ripen until the owner is on notice that the user claims a right; the seven-year time limit on suits to recover land (Ark. Code Ann. § 18-61-101) is a related statute, and adverse possession of title separately requires color of title and tax payment (§ 18-11-106).",
    ],
  },
  {
    stateCode: "CA",
    stateName: "California",
    years: 5,
    citation: "Cal. Civ. Proc. Code §§ 321, 325; Cal. Civ. Code § 1007",
    officialUrl: "https://leginfo.legislature.ca.gov/faces/codes.xhtml",
    notes: [
      "Five years of open, notorious, continuous, and hostile use.",
      "Unlike adverse possession, a prescriptive easement claimant does not have to pay the taxes on the strip.",
      "A recorded notice under Civ. Code § 813 can defeat a claim of public prescriptive rights.",
    ],
  },
  {
    stateCode: "CO",
    stateName: "Colorado",
    years: 18,
    citation: "Colo. Rev. Stat. § 38-41-101",
    officialUrl: "https://leg.colorado.gov/colorado-revised-statutes",
    notes: [
      "Eighteen years of adverse use; seven years applies with color of title and payment of taxes.",
      "Colorado presumes adversity from long, uninterrupted use, shifting the burden to the owner.",
    ],
  },
  {
    stateCode: "CT",
    stateName: "Connecticut",
    years: 15,
    citation: "Conn. Gen. Stat. § 47-37",
    officialUrl: "https://www.cga.ct.gov/current/pub/titles.htm",
    notes: [
      "Fifteen years of uninterrupted use under a claim of right.",
      "An owner can interrupt the period by recording and serving a notice under § 47-38.",
    ],
  },
  {
    stateCode: "DE",
    stateName: "Delaware",
    years: 20,
    citation: "Del. Code Ann. tit. 10, § 7901; common law prescription",
    officialUrl: "https://delcode.delaware.gov/",
    notes: ["Twenty years of adverse, continuous use is the accepted period.", PUBLIC_LAND_NOTE],
  },
  {
    stateCode: "DC",
    stateName: "District of Columbia",
    years: 15,
    citation: "D.C. Code § 16-1113; common law prescription",
    officialUrl: "https://code.dccouncil.gov/us/dc/council/code/titles/16",
    notes: [
      "Fifteen years of open, adverse, continuous use.",
      "Claims against District-owned property are barred.",
    ],
  },
  {
    stateCode: "FL",
    stateName: "Florida",
    years: 20,
    adversePossessionYears: 7,
    citation: "Fla. Stat. § 95.361; Downing v. Bird, 100 So. 2d 57 (Fla. 1958)",
    officialUrl: "https://www.leg.state.fl.us/statutes/",
    notes: [
      "Twenty years of adverse use for a prescriptive easement, proven by clear and positive evidence.",
      "Adverse possession of title runs only seven years with color of title or paid taxes.",
      "Roads maintained by a public authority for four years can become public under § 95.361.",
    ],
  },
  {
    stateCode: "GA",
    stateName: "Georgia",
    years: 20,
    adversePossessionYears: 7,
    citation: "Ga. Code Ann. §§ 44-9-1, 44-5-163",
    officialUrl: "https://www.legis.ga.gov/legislation/laws",
    notes: [
      "Twenty years of uninterrupted use; seven years applies where the use is under written color of title.",
      "A private way must be kept in repair by the user for the period to count.",
    ],
  },
  {
    stateCode: "HI",
    stateName: "Hawaii",
    years: 20,
    citation: "Haw. Rev. Stat. § 657-31",
    officialUrl: "https://www.capitol.hawaii.gov/docs/hrs.htm",
    notes: [
      "Twenty years of adverse use.",
      "Native Hawaiian traditional and gathering rights are separate from prescription and are constitutionally protected.",
    ],
  },
  {
    stateCode: "ID",
    stateName: "Idaho",
    years: 20,
    citation: "Idaho Code § 5-203",
    officialUrl: "https://legislature.idaho.gov/statutesrules/idstat/",
    notes: [
      "Twenty years of open, notorious, continuous, and adverse use.",
      "Use of unenclosed rangeland is presumed permissive under Idaho's open-range tradition.",
    ],
  },
  {
    stateCode: "IL",
    stateName: "Illinois",
    years: 20,
    citation: "735 ILCS 5/13-101; common law prescription",
    officialUrl: "https://www.ilga.gov/legislation/ilcs/ilcs.asp",
    notes: [
      "Twenty years of adverse, exclusive, continuous, and uninterrupted use under claim of right.",
      "Seven years applies to possession with color of title and payment of taxes.",
    ],
  },
  {
    stateCode: "IN",
    stateName: "Indiana",
    years: 20,
    citation: "Ind. Code § 32-23-1-1; Fraley v. Minger, 829 N.E.2d 476 (Ind. 2005)",
    officialUrl: "https://iga.in.gov/laws/current/ic/titles/32",
    notes: [
      "Twenty years of adverse use, and the claimant must also have paid taxes on the interest where taxes are assessed.",
      "Indiana requires clear and convincing evidence of control, intent, notice, and duration.",
    ],
  },
  {
    stateCode: "IA",
    stateName: "Iowa",
    years: 10,
    citation: "Iowa Code § 564.1",
    officialUrl: "https://www.legis.iowa.gov/law/iowaCode",
    notes: [
      "Ten years of hostile, continuous use, but Iowa requires express notice of the adverse claim. Mere long use is not enough.",
      "Section 564.1 bars claims founded solely on adverse use without that notice.",
    ],
  },
  {
    stateCode: "KS",
    stateName: "Kansas",
    years: 15,
    citation: "Kan. Stat. Ann. § 60-503",
    officialUrl: "https://ksrevisor.gov/ksa.html",
    notes: [
      "Fifteen years of open, exclusive, continuous possession or use, either knowingly adverse or under belief of ownership.",
    ],
  },
  {
    stateCode: "KY",
    stateName: "Kentucky",
    years: 15,
    citation: "Ky. Rev. Stat. Ann. § 413.010",
    officialUrl: "https://apps.legislature.ky.gov/law/statutes/",
    notes: [
      "Fifteen years of adverse, continuous use.",
      "Passway easements are frequently litigated; Kentucky presumes permissive use among family members.",
    ],
  },
  {
    stateCode: "LA",
    stateName: "Louisiana",
    years: 30,
    adversePossessionYears: 10,
    citation: "La. Civ. Code arts. 740-742, 3486",
    officialUrl: "https://www.legis.la.gov/legis/Laws_Toc.aspx?folder=67&level=Parent",
    notes: [
      "Louisiana uses servitudes and acquisitive prescription: thirty years without title, or ten years in good faith with just title.",
      "Only apparent and continuous servitudes, visible works such as a road or a drain, can be acquired by prescription.",
    ],
  },
  {
    stateCode: "ME",
    stateName: "Maine",
    years: 20,
    citation: "Me. Rev. Stat. tit. 14, § 812",
    officialUrl: "https://legislature.maine.gov/statutes/",
    notes: [
      "Twenty years of continuous, adverse use under claim of right with the owner's knowledge.",
      "A recorded notice under tit. 14, § 812 interrupts the running of the period.",
    ],
  },
  {
    stateCode: "MD",
    stateName: "Maryland",
    years: 20,
    citation: "Md. Code Ann., Cts. & Jud. Proc. § 5-103",
    officialUrl: "https://mgaleg.maryland.gov/mgawebsite/Laws/StatuteText",
    notes: [
      "Twenty years of adverse, exclusive, continuous use.",
      "Long, unexplained use raises a presumption of adversity that the owner must rebut.",
    ],
  },
  {
    stateCode: "MA",
    stateName: "Massachusetts",
    years: 20,
    citation: "Mass. Gen. Laws ch. 187, § 2; ch. 260, § 21",
    officialUrl: "https://malegislature.gov/Laws/GeneralLaws",
    notes: [
      "Twenty years of uninterrupted, open, notorious, adverse use.",
      "A recorded notice under ch. 187, § 3 prevents the acquisition of prescriptive rights.",
      "Registered (Land Court) land cannot be taken by prescription.",
    ],
  },
  {
    stateCode: "MI",
    stateName: "Michigan",
    years: 15,
    citation: "Mich. Comp. Laws § 600.5801",
    officialUrl: "https://www.legislature.mi.gov/Laws/MCL",
    notes: [
      "Fifteen years of open, notorious, adverse, continuous use.",
      "Periods of successive users can be tacked together where privity exists.",
    ],
  },
  {
    stateCode: "MN",
    stateName: "Minnesota",
    years: 15,
    citation: "Minn. Stat. § 541.02",
    officialUrl: "https://www.revisor.mn.gov/statutes/",
    notes: [
      "Fifteen years of hostile, actual, open, continuous, exclusive use.",
      "Torrens-registered property is generally immune from prescriptive claims.",
    ],
  },
  {
    stateCode: "MS",
    stateName: "Mississippi",
    years: 10,
    citation: "Miss. Code Ann. § 15-1-13",
    officialUrl: "https://law.justia.com/codes/mississippi/",
    notes: [
      "Ten years of open, notorious, hostile, continuous use under claim of ownership.",
      "Sixteenth-section school trust land is not subject to prescription.",
    ],
  },
  {
    stateCode: "MO",
    stateName: "Missouri",
    years: 10,
    citation: "Mo. Rev. Stat. § 516.010",
    officialUrl: "https://revisor.mo.gov/main/Home.aspx",
    notes: [
      "Ten years of continuous, visible, adverse use under claim of right.",
      "Missouri does not require exclusivity for a prescriptive easement in the way it does for possession.",
    ],
  },
  {
    stateCode: "MT",
    stateName: "Montana",
    years: 5,
    citation: "Mont. Code Ann. §§ 70-19-401, 70-19-405",
    officialUrl: "https://leg.mt.gov/bills/mca/",
    notes: [
      "Five years of open, notorious, exclusive, adverse, continuous use.",
      "Adverse possession of title also requires payment of all taxes during the period.",
    ],
  },
  {
    stateCode: "NE",
    stateName: "Nebraska",
    years: 10,
    citation: "Neb. Rev. Stat. § 25-202",
    officialUrl: "https://nebraskalegislature.gov/laws/browse-statutes.php",
    notes: ["Ten years of open, notorious, exclusive, adverse, continuous use.", PUBLIC_LAND_NOTE],
  },
  {
    stateCode: "NV",
    stateName: "Nevada",
    years: 5,
    citation: "Nev. Rev. Stat. §§ 11.070, 11.150",
    officialUrl: "https://www.leg.state.nv.us/nrs/",
    notes: [
      "Five years of adverse use, and the claimant must have paid all taxes assessed during that period.",
      "Federal land, which covers most of Nevada, is not subject to prescription.",
    ],
  },
  {
    stateCode: "NH",
    stateName: "New Hampshire",
    years: 20,
    citation: "N.H. Rev. Stat. Ann. § 508:2",
    officialUrl: "https://www.gencourt.state.nh.us/rsa/html/indexes/default.html",
    notes: [
      "Twenty years of adverse, continuous, uninterrupted use with the owner's knowledge.",
      "Recreational use of open land is generally treated as permissive.",
    ],
  },
  {
    stateCode: "NJ",
    stateName: "New Jersey",
    years: 20,
    adversePossessionYears: 60,
    citation: "N.J. Stat. Ann. § 2A:14-6",
    officialUrl: "https://law.justia.com/codes/new-jersey/",
    notes: [
      "Twenty years of adverse use for a prescriptive easement.",
      "Adverse possession of uncultivated woodland or marsh requires sixty years; thirty years for other land.",
    ],
  },
  {
    stateCode: "NM",
    stateName: "New Mexico",
    years: 10,
    citation: "N.M. Stat. Ann. § 37-1-22",
    officialUrl: "https://www.nmcompcomm.us/search-laws/",
    notes: [
      "Ten years of open, uninterrupted, peaceable, notorious, adverse use.",
      "Adverse possession additionally requires color of title and payment of taxes.",
    ],
  },
  {
    stateCode: "NY",
    stateName: "New York",
    years: 10,
    citation: "N.Y. Real Prop. Acts. Law §§ 501, 511",
    officialUrl: "https://www.nysenate.gov/legislation/laws/RPA",
    notes: [
      "Ten years of open, notorious, continuous, hostile use.",
      "The 2008 amendments narrowed claims: de minimis encroachments such as lawn mowing and fences are deemed permissive.",
    ],
  },
  {
    stateCode: "NC",
    stateName: "North Carolina",
    years: 20,
    adversePossessionYears: 7,
    citation: "N.C. Gen. Stat. §§ 1-40, 1-38",
    officialUrl: "https://www.ncleg.gov/Laws/GeneralStatutes",
    notes: [
      "Twenty years of adverse use; seven years applies where possession is under color of title.",
      "North Carolina presumes use of another's land is permissive, so the claimant must prove hostility.",
    ],
  },
  {
    stateCode: "ND",
    stateName: "North Dakota",
    years: 20,
    citation: "N.D. Cent. Code §§ 28-01-04, 47-05-12",
    officialUrl: "https://ndlegis.gov/general-information/north-dakota-century-code",
    notes: [
      "Twenty years of adverse use; ten years applies with color of title and payment of taxes.",
      "Easements not used for twenty years may be treated as abandoned.",
    ],
  },
  {
    stateCode: "OH",
    stateName: "Ohio",
    years: 21,
    citation: "Ohio Rev. Code Ann. § 2305.04",
    officialUrl: "https://codes.ohio.gov/ohio-revised-code",
    notes: [
      "Twenty-one years of open, notorious, adverse, continuous use.",
      "Ohio's Marketable Title Act can extinguish old recorded easements not preserved within forty years.",
    ],
  },
  {
    stateCode: "OK",
    stateName: "Oklahoma",
    years: 15,
    citation: "Okla. Stat. tit. 12, § 93",
    officialUrl: "https://www.oscn.net/applications/oscn/index.asp?ftdb=STOKST",
    notes: ["Fifteen years of open, notorious, hostile, continuous use.", PUBLIC_LAND_NOTE],
  },
  {
    stateCode: "OR",
    stateName: "Oregon",
    years: 10,
    citation: "Or. Rev. Stat. § 12.050",
    officialUrl: "https://www.oregonlegislature.gov/bills_laws/Pages/ORS.aspx",
    notes: [
      "Ten years of open, notorious, adverse, continuous use, proven by clear and convincing evidence.",
      "A recorded notice under ORS 105.688 can preserve permissive public recreational access.",
    ],
  },
  {
    stateCode: "PA",
    stateName: "Pennsylvania",
    years: 21,
    citation: "42 Pa. Cons. Stat. § 5530",
    officialUrl: "https://www.legis.state.pa.us/cfdocs/legis/LI/Public/cons_index.cfm",
    notes: [
      "Twenty-one years of adverse, open, notorious, continuous use.",
      "Since 2019, residential lots of a half acre or less may qualify for a ten-year adverse possession path.",
    ],
  },
  {
    stateCode: "RI",
    stateName: "Rhode Island",
    years: 10,
    citation: "R.I. Gen. Laws § 34-7-1",
    officialUrl: "https://webserver.rilegislature.gov/Statutes/",
    notes: [
      "Ten years of uninterrupted, quiet, peaceable, adverse use.",
      "Recording a notice under § 34-7-6 interrupts the period.",
    ],
  },
  {
    stateCode: "SC",
    stateName: "South Carolina",
    years: 20,
    adversePossessionYears: 10,
    citation: "S.C. Code Ann. § 15-67-210; common law prescription",
    officialUrl: "https://www.scstatehouse.gov/code/statmast.php",
    notes: [
      "Twenty years of continuous, adverse use for a prescriptive easement.",
      "Adverse possession of title runs ten years.",
    ],
  },
  {
    stateCode: "SD",
    stateName: "South Dakota",
    years: 20,
    citation: "S.D. Codified Laws §§ 15-3-1, 43-13-3",
    officialUrl: "https://sdlegislature.gov/Statutes",
    notes: [
      "Twenty years of adverse use; ten years applies with color of title and payment of taxes.",
    ],
  },
  {
    stateCode: "TN",
    stateName: "Tennessee",
    years: 20,
    adversePossessionYears: 7,
    citation: "Tenn. Code Ann. § 28-2-103; common law prescription",
    officialUrl: "https://law.justia.com/codes/tennessee/",
    notes: [
      "Twenty years of adverse, continuous use for a prescriptive easement.",
      "Adverse possession under color of title with paid taxes runs seven years.",
    ],
  },
  {
    stateCode: "TX",
    stateName: "Texas",
    years: 10,
    citation: "Tex. Civ. Prac. & Rem. Code §§ 16.026, 16.024-16.025",
    officialUrl: "https://statutes.capitol.texas.gov/",
    notes: [
      "Ten years of open, notorious, hostile, exclusive, continuous use.",
      "Three- and five-year paths exist for possession under title or a recorded deed with paid taxes.",
      "Texas prescriptive easement claims fail if the use was shared with the owner, because exclusivity is required.",
    ],
  },
  {
    stateCode: "UT",
    stateName: "Utah",
    years: 20,
    citation: "Marchant v. Park City, 788 P.2d 520 (Utah 1990); Valcarce v. Fitzgerald, 961 P.2d 305 (Utah 1998)",
    officialUrl: "https://le.utah.gov/xcode/code.html",
    notes: [
      "Twenty years of open, notorious, continuous, adverse use.",
      "Adverse possession of title requires seven years plus payment of all taxes.",
    ],
  },
  {
    stateCode: "VT",
    stateName: "Vermont",
    years: 15,
    citation: "Vt. Stat. Ann. tit. 12, § 501",
    officialUrl: "https://legislature.vermont.gov/statutes/",
    notes: [
      "Fifteen years of open, notorious, hostile, continuous use.",
      "Public and school trust lands are exempt.",
    ],
  },
  {
    stateCode: "VA",
    stateName: "Virginia",
    years: 20,
    citation: "Va. Code Ann. § 8.01-236; common law prescription",
    officialUrl: "https://law.lis.virginia.gov/vacode/",
    notes: [
      "Twenty years of open, visible, continuous, adverse use, shown by clear and convincing evidence.",
      "Long use raises a presumption of a grant that the owner must rebut.",
    ],
  },
  {
    stateCode: "WA",
    stateName: "Washington",
    years: 10,
    citation: "Wash. Rev. Code § 4.16.020",
    officialUrl: "https://app.leg.wa.gov/rcw/",
    notes: [
      "Ten years of open, notorious, hostile, continuous use.",
      "Seven years applies with color of title and payment of taxes.",
      "Use of unenclosed, vacant land is presumed permissive.",
    ],
  },
  {
    stateCode: "WV",
    stateName: "West Virginia",
    years: 10,
    citation: "W. Va. Code § 55-2-1; common law prescription",
    officialUrl: "https://code.wvlegislature.gov/",
    notes: [
      "Ten years of open, continuous, adverse use under claim of right.",
      "Severed mineral and surface estates complicate many access claims in the state.",
    ],
  },
  {
    stateCode: "WI",
    stateName: "Wisconsin",
    years: 20,
    citation: "Wis. Stat. §§ 893.28, 893.25",
    officialUrl: "https://docs.legis.wisconsin.gov/statutes/prefaces/toc",
    notes: [
      "Twenty years of uninterrupted adverse use creates an easement by prescription.",
      "Adverse possession of title runs twenty years, or ten with color of title.",
    ],
  },
  {
    stateCode: "WY",
    stateName: "Wyoming",
    years: 10,
    citation: "Wyo. Stat. Ann. § 1-3-103",
    officialUrl: "https://wyoleg.gov/StateStatutes/StatutesConstitution",
    notes: [
      "Ten years of open, notorious, continuous, hostile use.",
      "Fenced-out rangeland and permissive grazing arrangements rarely support a claim.",
    ],
  },
];

/** Alphabetical by state name, for the dropdown. */
export const PRESCRIPTIVE_EASEMENT_LAW_SORTED = [...PRESCRIPTIVE_EASEMENT_LAW].sort((a, b) =>
  a.stateName.localeCompare(b.stateName),
);

export function findStateLaw(stateCode: string | undefined | null): StateEasementLaw | undefined {
  if (!stateCode) return undefined;
  const code = stateCode.trim().toUpperCase();
  return PRESCRIPTIVE_EASEMENT_LAW.find((entry) => entry.stateCode === code);
}
